<template>
  <article id="edit-textstamps" :class="{}">
    <div class="inputs-row-container">
      <!-- Text Input -->
      <label
        class="input-label input-in-row"
        for="textstamp-name"
        id="text-input"
      >
        <span>Text</span>
        <input
          class="input-label"
          type="text"
          id="textstamp-name"
          @input="(e) => handleTextStampValue(e.target.value)"
          :value="textValue"
        />
        <!-- :value="textStampInputValue || ''" -->
      </label>

      <!-- Fonts dropdown -->
      <label class="input-label input-in-row" id="fonts-dropdown">
        <span>Font</span>
        <v-select
          v-model="textStampFont"
          label="name"
          :options="threekitAvailableFonts"
          :clearable="false"
          :searchable="false"
          :disabled="
            threekitAvailableFonts && threekitAvailableFonts.length < 2
          "
          class="box-shadow"
          id="change-fonts"
        >
          <template #selected-option>
            <img
              class="font-option selected"
              :src="`${baseUrl}Images/Fonts/${getFontBackgroundName(
                textStampFont.label
              )}.png`"
              :alt="textStampFont.label"
            />
          </template>
          <template slot="option" slot-scope="option">
            <img
              class="font-option"
              :src="`${baseUrl}Images/Fonts/${getFontBackgroundName(
                option.name
              )}.png`"
              :alt="option.name"
            />
          </template>
        </v-select>
      </label>

      <!-- Size Dropdown -->
      <label
        class="input-label input-in-row"
        v-if="showSizesDropdown"
        id="size-dropdown"
      >
        <p>Height</p>
        <v-select
          :value="sizeValue"
          @input="(size) => $emit('changeSize', size)"
          :options="sizeOptions"
          :clearable="false"
          :searchable="false"
          :disabled="sizeOptions && sizeOptions.length < 2"
          class="box-shadow"
        ></v-select>
      </label>
    </div>

    <div class="colors-picker">
      <span>Colors</span>
      <ColorPicker
        :colorPickerMode="'multiple'"
        :colorsToChange="textStampColors"
        :colorsOptionsOverride="activeDecorationAvailableColors"
        :allowNoneColorOption="true"
        @selectColor="(color) => handleTextStampColor(color)"
      />
    </div>

    <div class="inputs-row-container">
      <!-- Decoration Location -->
      <label
        class="input-label"
        v-if="locationOptions && locationOptions.length"
        id="change-location"
      >
        <span class="label">Location</span>
        <v-select
          :value="currentLocation"
          @input="(location) => $emit('changeLocation', location)"
          label="name"
          :options="locationOptions"
          :clearable="false"
          :searchable="false"
          :disabled="locationOptions && locationOptions.length < 2"
          class="box-shadow"
        ></v-select>
      </label>
      <!-- Material Dropdown -->
      <label
        class="input-label input-in-row"
        v-if="showMaterialsDropdown"
        id="change-material"
        :class="{ disabled: materialsOptions && materialsOptions.length < 2 }"
      >
        <span class="label">Type</span>
        <v-select
          :value="materialValue"
          @input="(material) => $emit('changeMaterial', material)"
          :options="materialsOptions"
          :clearable="false"
          :searchable="false"
          :disabled="materialsOptions && materialsOptions.length < 2"
          class="box-shadow"
        >
          <!-- Show this only if the dropdown is disabled -->
          <template
            #selected-option
            v-if="materialsOptions && materialsOptions.length < 2"
          >
            <div class="disabled-display-container">
              <img src="@/assets/icons/ui/lock-closed-outline.svg" alt="lock" />
              <span class="text-placeholder">{{ materialValue }}</span>
            </div>
          </template>
        </v-select>
      </label>
    </div>
  </article>
</template>

<script>
import ColorPicker from "@/components/Projects/Project/Colors/ColorPicker.vue";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import CONSTANTS from "@/constants";
import { debounce } from "debounce";
export default {
  name: "AddTextStamp",
  components: { ColorPicker },
  data() {
    return {
      fonts: CONSTANTS.FONTS,
      baseUrl: CONSTANTS.S3_BASE_URL,
      useBackupColor: false,
      tempValue: 0,
    };
  },

  props: {
    decoration: {
      type: Object,
      required: true,
    },
    activeDecorationConfigurator: {
      type: Object,
      required: true,
    },
    activeDecorationAttributes: {
      type: Array,
      required: true,
    },
    showLocationDropdown: {
      type: Boolean,
      default: false,
    },
    currentLocation: {
      type: String,
      default: "",
    },
    locationOptions: {
      type: Array,
    },
    availableLocationsByContentType: {
      type: Object,
    },
    materialsConfiguration: {
      type: Object,
    },
    materialsOptions: {
      type: Array,
    },
    showMaterialsDropdown: {
      type: Boolean,
      default: false,
    },
    showSizesDropdown: {
      type: Boolean,
      default: false,
    },
    sizeConfiguration: {
      type: Object,
    },
    sizeOptions: {
      type: Array,
    },
    sizeValue: {
      type: String,
      default: "",
    },
    textValue: {
      type: String,
      default: "",
    },
  },

  computed: {
    ...mapGetters("project", [
      "getColorById",
      "isSnapsheet",
      "projectColors",
      "getProductTextStamp",
      "is3DAvailable",
    ]),

    ...mapState("threekit", {
      threekitFonts: (state) => state.fonts,
      activeDecoration: (state) => state.activeDecoration,
      sublimationColors: (state) => state.sublimationColors,
      stockColors: (state) => state.stockColors,
    }),

    ...mapState("threekit", ["materialColors"]),

    ...mapGetters("threekit", [
      "getNestedConfigurator",
      "getThreekitColorAssetIdByGameplanId",
      "findThreekitColorByAssetId",
    ]),

    textStampColors() {
      //Check if the decoration allows a second outline

      const allowsFirstOutline = this.activeDecorationAttributes?.find(
        (attribute) => attribute.name === "Text Outline 1"
      );

      const allowsSecondOutline = this.activeDecorationAttributes?.find(
        (attribute) => attribute.name === "Text Outline 2"
      );

      const colors = [this.textStampFillColor];

      if (allowsFirstOutline) {
        colors.push(this.textStampOutlineColor1);
      }

      if (allowsSecondOutline) {
        colors.push(this.textStampOutlineColor2);
      }

      return colors;
    },

    textStampFillColor() {
      return this.getPickerColor("Text Fill", "fill");
    },

    textStampOutlineColor1() {
      return this.getPickerColor("Text Outline 1", "outline 1");
    },

    textStampOutlineColor2() {
      return this.getPickerColor("Text Outline 2", "outline 2");
    },

    //Threekit

    activeDecorationColors() {
      return this.activeDecorationAttributes?.filter((attr) =>
        attr?.name?.includes("Text Fill")
      )?.[0]?.values;
    },

    activeDecorationColorAreSublimated() {
      return this.activeDecorationColors?.[0]?.tags.includes(
        "type_sublimation-color"
      );
    },

    activeDecorationColorsPalette() {
      return this.activeDecorationColorAreSublimated
        ? this.sublimationColors
        : this.stockColors;
    },

    activeDecorationAvailableColors() {
      //Exception -> Sublimation Material returns sublimation pallete directly
      if (this.materialValue?.toLowerCase() === "sublimation")
        return this.sublimationColors;

      const materialColors = this.materialColors
        ? this.materialColors?.[this.materialValue?.toLowerCase()]
        : [];

      const colors = this.activeDecorationColorAreSublimated
        ? this.sublimationColors
        : this.stockColors;

      return colors?.filter((color) => {
        return (
          materialColors
            ?.map((mc) => {
              return mc.name.toLowerCase();
            })
            .indexOf(color.name.toLowerCase()) >= 0
        );
      });
    },

    //The available fonts depends on the 'Content Type' attribute of the text
    threekitAvailableFonts() {
      const fonts = this.activeDecorationAttributes?.filter(
        (attr) => attr.name === "Font"
      )[0]?.values;

      return fonts?.filter((availableFont) =>
        this.threekitFonts?.find((font) => font.name === availableFont.name)
      );
    },

    //The available fonts depends on the c'Content Type' attribute of the text
    threekitAvailableContentTypes() {
      const allTypes = this.activeDecorationAttributes
        ?.filter((attr) => attr.name === "Content Type")[0]
        ?.values?.filter((v) => v.value !== "Graphics" && v.value !== "Uploads")
        .map((v) => v.value);

      //We need to check if this types are available for the current location
      const availableTypes = allTypes?.filter(
        (type) =>
          this.availableLocationsByContentType &&
          this.availableLocationsByContentType[type]?.includes(
            this.activeDecoration.locationName
          )
      );

      return availableTypes;
    },

    //Dropdowns

    contentType: {
      get() {
        return this.activeDecorationAttributes?.filter(
          (attr) => attr.name === "Content Type"
        )[0]?.value;
      },
      async set(textContentType) {
        this.setLoadingState({
          loadingState: true,
          message: "Applying Changes...",
        });
        setTimeout(() => {
          this.$nextTick(async () => {
            try {
              //next tick
              //Save changes in the active decoration
              await this.setActiveDecorationChanges({
                decorationPropertyName: "Content Type",
                decorationPropertyValue: textContentType,
              });
            } catch (error) {
              console.error(error);
            } finally {
              this.setLoadingState({ loadingState: false });
            }
          });
        }, 100);
      },
    },

    textStampFont: {
      get() {
        const activeFontId = this.activeDecorationAttributes?.filter(
          (attr) => attr.name === "Font"
        )[0]?.value?.assetId;
        console.log("activeFontId", activeFontId);
        return this.threekitAvailableFonts?.find(
          (font) => font.assetId === activeFontId
        );
      },
      async set(font) {
        this.setLoadingState({
          loadingState: true,
          message: "Applying Changes...",
        });
        setTimeout(() => {
          this.$nextTick(async () => {
            try {
              //Save changes in the active decoration
              await this.setActiveDecorationChanges({
                decorationPropertyName: "Font",
                decorationPropertyValue: {
                  assetId: font.assetId,
                },
              });
            } catch (error) {
              console.error(error);
            } finally {
              this.setLoadingState({ loadingState: false });
            }
          });
        }, 100);
      },
    },

    materialValue() {
      return (
        this.materialsConfiguration &&
        (this.materialsConfiguration?.value ||
          this.materialsConfiguration?.defaultValue ||
          this.materialsConfiguration?.values?.[
            this.materialsConfiguration?.values?.length - 1
          ].value)
      );
    },
  },

  methods: {
    ...mapActions("project", ["applyProductDecorationsChanges"]),
    ...mapActions("threekit", [
      "setActiveDecorationChanges",
      "setActiveDecorationLocation",
      "updateDecorationColor",
      "updateDecorationTextValue",
      "updateTextContentType",
      "updateTextFont",
      "setLoadingState",
    ]),
    ...mapMutations("project", ["SET_GLOBAL_UNIFORMS_DECORATIONS"]),

    emitChangeTextStampSelected(value) {
      this.$emit("changeTextStampSelected", value);
    },

    getFontBackgroundName(fontName) {
      return fontName?.replace(/\s/g, "_").toLowerCase();
    },

    getPickerColor(threekitColorKey, colorKey) {
      const colorAttribute = this.activeDecorationAttributes?.filter(
        (attr) => attr.name === threekitColorKey
      )[0];
      console.log("colorAttribute ", colorAttribute);

      const colorAssetId = colorAttribute?.value?.assetId;
      //If there is no custom fill, return a default color.
      //For playbooks -> The second color of the project
      //For snaspheet -> black

      const colorObject = {
        key: colorKey,
        colorKey: colorKey,
        label: colorKey,
        color: this.findThreekitColorByAssetId(
          colorAssetId,
          this.useBackupColor
        ),
      };

      return colorObject;
    },

    handleTextStampValue: debounce(async function (value) {
      try {
        this.setLoadingState({
          loadingState: true,
          message: "Applying Changes...",
        });
        this.tempValue = value;
        setTimeout(() => {
          this.$nextTick(async () => {
            console.log("temp value: ", this.tempValue);
            try {
              //Save changes in the active decoration
              await this.setActiveDecorationChanges({
                decorationPropertyName: "Text",
                decorationPropertyValue: this.tempValue,
              });
            } catch (error) {
              console.error(error);
            } finally {
              this.setLoadingState({ loadingState: false });
            }
          });
        }, 100);
      } catch (error) {
        console.log(error);
      }
    }, 1500),

    async handleTextStampColor(color) {
      console.log("handleTextStampColor color", color);
      const decorationPropertyName =
        color.colorKey === "fill"
          ? "Text Fill"
          : color.colorKey === "outline_1"
          ? "Text Outline 1"
          : "Text Outline 2";

      this.setLoadingState({
        loadingState: true,
        message: "Applying Changes...",
      });
      setTimeout(() => {
        this.$nextTick(async () => {
          try {
            //Save changes in the active decoration
            await this.setActiveDecorationChanges({
              decorationPropertyName,
              decorationPropertyValue: {
                assetId: color.color.assetId
                  ? color.color.assetId
                  : color.color.id,
              },
            });
          } catch (error) {
            console.error(error);
          } finally {
            this.setLoadingState({ loadingState: false });
          }
        });
      }, 100);
    },
  },
};
</script>

<style lang="scss" scoped>
#edit-textstamps {
  display: flex;
  flex-direction: column;

  &.snapsheet-temporal-product-template {
    #change-fonts {
      order: 1;
      margin-bottom: 1rem;
      width: 100%;
    }
    .inputs-row-container {
      order: 2;
      margin-bottom: 1rem;

      .input-in-row {
        width: 100%;
      }
    }
    .colors-picker {
      order: 3;

      .selected-colors {
        flex-direction: column;
      }
    }
  }

  .colors-picker {
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
    span {
      margin-bottom: 0.5rem;
      text-transform: uppercase;
    }
  }

  .inputs-row-container {
    display: flex;
    justify-content: flex-start;
    gap: 2rem;
    margin-bottom: 1rem;
    width: 100%;
    padding: 0.25rem 0;
    background: white;
    /* border: 1px solid $grey-light; */
    border-radius: 6px;

    .input-label {
      display: flex;
      flex-direction: column;
      margin-bottom: 0;
      width: 100%;
      &.input-in-row {
        width: 40%;

        &#text-input,
        &#fonts-dropdown {
          width: 35%;
        }
        &#size-dropdown {
          width: 10%;
        }

        @media screen and (max-width: 1440px) {
          &#text-input,
          &#fonts-dropdown {
            width: 30%;
          }
          &#size-dropdown {
            width: 15%;
          }
        }
      }

      span,
      p {
        margin-bottom: 0.5rem;
        text-transform: uppercase;
      }

      input {
        width: 100%;
        padding: 0.5rem 0.75rem;
        background: white;
        border: 1px solid $grey-light;
        border-radius: 6px;
        height: 3rem;
      }

      @media screen and (max-width: 768px) {
        width: 100% !important;
      }
    }

    &.select {
      padding: 0;
      border: none;
      ::v-deep {
        .vs__dropdown-toggle {
          border-radius: 6px;
          border: 1px solid $grey-light;
          padding: 0.45rem;
          height: 3rem;
        }
      }
    }
    .font-option {
      max-width: 100%;
      height: 20px;
      display: flex;

      &:not(.selected) {
        margin: 1rem auto;
      }
    }
    .v-select {
      ::v-deep .vs__dropdown-toggle {
        border-radius: 6px;
        border: 1px solid $grey-light;
        padding: 0.45rem;

        .vs__selected-options {
          .vs__selected {
            /* color: transparent; */
            background-position: center;
            background-size: contain;
            background-repeat: no-repeat;
            text-transform: capitalize;

            // font-family: "SlateStd-Bold", arial, sans-serif !important;
          }
        }

        input {
          position: absolute;
          background-color: transparent;
        }
      }
    }

    #change-content-type,
    #change-location,
    #change-material {
      ::v-deep span.label {
        text-transform: uppercase;
      }
    }
    #change-location,
    #change-material {
      width: 40%;
    }
    .disabled-display-container {
      display: flex;
      align-items: center;
      img {
        margin-right: 0.5rem;
      }
      span {
        text-transform: capitalize;
        margin-bottom: 0;
      }
    }

    #change-material.disabled {
      cursor: not-allowed !important;

      ::v-deep .vs__selected {
        color: $placeholder;
      }

      ::v-deep .vs__dropdown-toggle {
        background: #fff;
        cursor: not-allowed !important;
      }
      ::v-deep .vs__actions {
        display: none;
        pointer-events: none;
      }
    }

    @media screen and (max-width: 768px) {
      flex-flow: column;
      width: 100%;
    }
  }
}
</style>
